// Steps/LinkNumberStep.jsx
// Points the wizard's purchased number at the receptionist via the EXISTING
// thirdPartyVapiLinkTwilio action. Shows "Linked ✓" on success.

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Label, Input, FormGroup, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  thirdPartyVapiLinkTwilio,
  thirdPartyVapiLinkTwilioSuccess,
} from "../../../../store/thirdPartyIntegration/action";

export function LinkNumberStep({ wizard }) {
  const {
    dispatch,
    receptionistId,
    purchasedNumber,
    selectedNumber,
    twilioLinked,
    setTwilioLinked,
    goNext,
    goBack,
  } = wizard;

  // Prefer the confirmed purchased number; fall back to the selected one.
  const numberToLink = purchasedNumber || selectedNumber || "";

  const { linkTwilioDataAPI, linkTwilioLoading } = useSelector((state) => ({
    linkTwilioDataAPI: state.ThirdPartyIntegration.thirdPartyVapiLinkTwilioData,
    linkTwilioLoading:
      state.ThirdPartyIntegration.thirdPartyVapiLinkTwilioLoading,
  }));

  useEffect(() => {
    if (isEmpty(linkTwilioDataAPI)) return;
    if (linkTwilioDataAPI.twilio_linked || linkTwilioDataAPI.status === 200) {
      toast.success(linkTwilioDataAPI?.message || "Number linked");
      setTwilioLinked(true);
      dispatch(thirdPartyVapiLinkTwilioSuccess([]));
    } else {
      toast.error(
        linkTwilioDataAPI?.error ||
          linkTwilioDataAPI?.message ||
          "Link failed"
      );
      dispatch(thirdPartyVapiLinkTwilioSuccess([]));
    }
  }, [linkTwilioDataAPI]);

  const handleLink = () => {
    if (!numberToLink) {
      toast.error("No number to link");
      return;
    }
    if (!receptionistId) {
      toast.error("Create the receptionist first");
      return;
    }
    dispatch(
      thirdPartyVapiLinkTwilio([
        { Id: receptionistId, twilio_number: numberToLink },
      ])
    );
  };

  return (
    <div className="step-container">
      <div className="mb-4 pb-2 border-bottom">
        <h4 className="text-pastel-blue fw-bold mb-1">Link the number</h4>
        <p className="text-muted small mb-0">
          Point this number&apos;s voice line at your AI receptionist.
        </p>
      </div>

      <Card className="border-0 shadow-sm rounded-4 bg-white p-4 mb-2">
        <FormGroup className="mb-3">
          <Label>Number</Label>
          <Input type="text" value={numberToLink} readOnly />
        </FormGroup>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-primary rounded-pill px-4 fw-semibold"
            onClick={handleLink}
            disabled={linkTwilioLoading || !numberToLink}
          >
            {linkTwilioLoading ? (
              <>
                <Spinner size="sm" className="me-2" /> Linking…
              </>
            ) : (
              <>
                <i className="ri-link me-2"></i>
                {twilioLinked ? "Re-link" : "Link to receptionist"}
              </>
            )}
          </button>
          {twilioLinked ? (
            <span className="badge bg-success">Linked &#10003;</span>
          ) : (
            <span className="badge bg-secondary">Not linked</span>
          )}
        </div>
      </Card>

      <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-light rounded-pill px-4 fw-semibold text-muted"
          onClick={goBack}
          disabled={linkTwilioLoading}
        >
          <i className="ri-arrow-left-line me-2"></i> Back
        </button>
        <button
          type="button"
          className="btn btn-primary rounded-pill px-4 fw-semibold"
          onClick={goNext}
          disabled={!twilioLinked}
        >
          Next <i className="ri-arrow-right-line ms-2"></i>
        </button>
      </div>
    </div>
  );
}

LinkNumberStep.propTypes = {
  wizard: PropTypes.object.isRequired,
};
